import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Diamond } from 'lucide-react';
import { usePreferences } from '@/context/PreferencesContext';
import './celebration.css';

/**
 * CelebrationAnimation - Complete implementation with force close support
 * 
 * Key improvements:
 * - Uses framer-motion for smooth animations
 * - Fireworks particles generated once
 * - Force close support to immediately stop animations
 * - Proper cleanup of all timers
 */
const CelebrationAnimation = forwardRef(({ points, gems, onComplete, forceClose = false }, ref) => {
  const containerRef = useRef(null);
  const hasCompletedRef = useRef(false);
  const timersRef = useRef([]);
  const [showFlyingPoints, setShowFlyingPoints] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const { playCelebrationSound } = usePreferences();
  
  // Ensure points and gems are numbers (default to 0 if undefined/null)
  const displayPoints = points ?? 0;
  const displayGems = gems ?? 0;

  // Generate fireworks particles once (not in state)
  const particles = Array.from({ length: 25 }, (_, i) => {
    const angle = (i * 360) / 25;
    const angleRad = (angle * Math.PI) / 180;
    const distance = 100 + Math.random() * 50;
    return {
      id: `particle-${i}`,
      x: Math.cos(angleRad) * distance,
      y: Math.sin(angleRad) * distance,
      delay: i * 0.02,
      color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A'][i % 5],
    };
  });

  // Expose forceClose method via ref
  useImperativeHandle(ref, () => ({
    forceClose: () => {
      setIsVisible(false);
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
      hasCompletedRef.current = true;
      if (onComplete) {
        onComplete();
      }
    },
  }), [onComplete]);

  // Handle force close
  useEffect(() => {
    if (forceClose) {
      setIsVisible(false);
      // Clear all timers
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
      if (onComplete) {
        onComplete();
      }
    }
  }, [forceClose, onComplete]);

  useEffect(() => {
    // Don't start animation if force closed
    if (forceClose) {
      return;
    }

    // Play celebration sound when animation starts
    if ((displayPoints > 0 || displayGems > 0) && playCelebrationSound) {
      playCelebrationSound();
    }

    // Show flying points animation after message is displayed
    const flyingPointsTimer = setTimeout(() => {
      if (!forceClose && !hasCompletedRef.current) {
        setShowFlyingPoints(true);
      }
    }, 500);
    timersRef.current.push(flyingPointsTimer);

    // Auto-complete after animation duration
    const timer = setTimeout(() => {
      if (!hasCompletedRef.current && !forceClose && onComplete) {
        hasCompletedRef.current = true;
        onComplete();
      }
    }, 1000);
    timersRef.current.push(timer);

    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
    };
  }, [onComplete, displayPoints, displayGems, playCelebrationSound, forceClose]);

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          ref={containerRef}
          className="celebration-container"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
        >
          {/* Fireworks particles */}
          <AnimatePresence>
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="celebration-particle"
                style={{
                  left: '50%',
                  top: '50%',
                  backgroundColor: particle.color,
                  transform: `translate(calc(-50% + ${particle.x}px), calc(-50% + ${particle.y}px))`,
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0],
                  y: [0, -80, -160],
                }}
                transition={{
                  delay: particle.delay,
                  duration: 0.6,
                  ease: 'easeOut',
                }}
              />
            ))}
          </AnimatePresence>

          {/* Trophy icon */}
          <motion.div
            className="celebration-icon"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              duration: 0.6,
            }}
          >
            <Trophy className="w-24 h-24 text-yellow-400" fill="currentColor" />
          </motion.div>

      {/* Points display - initial */}
      {!showFlyingPoints && (
        <motion.div
          className="celebration-points"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <div className="points-badge">
            <Trophy className="w-5 h-5 text-yellow-400" fill="currentColor" />
            <span className="points-text">+{displayPoints} XP</span>
            {displayGems > 0 && (
              <>
                <span className="points-text">· </span>
                <Diamond className="w-5 h-5 text-purple-700 inline-block" />
                <span className="points-text"> +{displayGems} Gems</span>
              </>
            )}
          </div>
        </motion.div>
      )}

      {/* Flying points animation to top-right */}
      <AnimatePresence>
        {showFlyingPoints && (
          <>
            {/* XP Points flying to top-right */}
            <motion.div
              className="flying-points"
              initial={{
                x: 0,
                y: 0,
                scale: 1,
                opacity: 1,
              }}
              animate={{
                x: typeof window !== 'undefined' ? (window.innerWidth / 2) - 120 : 0,
                y: typeof window !== 'undefined' ? -(window.innerHeight / 2) + 100 : 0,
                scale: 0.6,
                opacity: 0,
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94], // easeOutQuad
              }}
            >
              <div className="flying-points-badge">
                <Trophy className="w-5 h-5 text-yellow-400" fill="currentColor" />
                <span className="points-text">+{displayPoints}</span>
              </div>
            </motion.div>

            {/* Gems flying to top-right (if applicable) */}
            {displayGems > 0 && (
              <motion.div
                className="flying-points"
                initial={{
                  x: 0,
                  y: 0,
                  scale: 1,
                  opacity: 1,
                }}
                animate={{
                  x: typeof window !== 'undefined' ? (window.innerWidth / 2) - 60 : 0,
                  y: typeof window !== 'undefined' ? -(window.innerHeight / 2) + 100 : 0,
                  scale: 0.6,
                  opacity: 0,
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
              >
                <div className="flying-points-badge gems">
                  <Diamond className="w-5 h-5 text-purple-700" />
                  <span className="points-text">+{displayGems}</span>
                </div>
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>

          {/* Congratulatory message */}
          <motion.div
            className="celebration-message"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <h2 className="message-title">🎉 Congratulations!</h2>
            <p className="message-subtitle">Activity completed successfully!</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

CelebrationAnimation.displayName = 'CelebrationAnimation';

export default CelebrationAnimation;
